import { FC, useState } from 'react';
import styled from 'styled-components';

import Icon from 'atoms/Icons/Icon';
import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';
import shadow from 'atoms/shadows/shadows';

import Flex from 'quarks/Flex';
import Text from 'quarks/Text';

import Modal from 'molecules/Modal/Modal';
import Select from 'molecules/Select/Select';

import ComponentButton from 'components/Button/ComponentButton';
import SidebarDemo from 'components/SidebarDemo';

type FieldsProps = {
  isModal?: boolean;
};

type TemplateHubFilterProps = {
  dropdownData: {
    industry: any[];
    jobStage: any[];
  };
  filterValues: {
    industry: string;
    stage: string;
  };
  tempFilterValues: {
    industry: string;
    stage: string;
  };
  setFilterValues: (values: { industry: string; stage: string }) => void;
  setTempFilterValues: (values: { industry: string; stage: string }) => void;
  PostCount: () => null;
};

const TemplateHubFilter: FC<TemplateHubFilterProps> = ({
  dropdownData,
  filterValues,
  setFilterValues,
  setTempFilterValues,
  tempFilterValues,
  PostCount,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleModalButtonClick = () => {
    setModalOpen(false);
    tempFilterValues && setFilterValues(tempFilterValues);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    filterValues && setTempFilterValues(filterValues);
  };

  const Fields: FC<FieldsProps> = ({ isModal }) => (
    <div className="filter-fields">
      {['industry', 'stage'].map(type => {
        const defaultValue = type === 'industry' ? 'All Industries' : 'All Stages of the Job';

        return (
          <>
            <Text as="p" textStyle="textMd" textColor="gray-900" fontWeight="semiBold">
              Filter by {type}
            </Text>
            <Select
              list={dropdownData?.[type === 'industry' ? 'industry' : 'jobStage'] || null}
              onChange={({ target: { value } }) => {
                const existingValues = isModal ? tempFilterValues : filterValues;

                const newValues = {
                  ...existingValues,
                  [type]: value === '' ? null : value,
                };

                isModal ? setTempFilterValues(newValues) : setFilterValues(newValues);
              }}
              defaultValue={
                isModal
                  ? tempFilterValues?.[type] || filterValues?.[type] || tempFilterValues?.[type] || defaultValue
                  : filterValues?.[type] || tempFilterValues?.[type] || tempFilterValues?.[type] || defaultValue
              }
              className={`${type}-select`}
            />
          </>
        );
      })}
    </div>
  );

  return (
    <Container>
      <div className="filter-desktop">
        <div className="filter-fields">
          <Fields />
        </div>
      </div>
      <Flex className="filter-mobile">
        <Flex className="filter-mobile-btn" onClick={() => setModalOpen(true)} role="presentation">
          <Icon id="filter-lines" fill={color.gray[900]} size={16} />
          <Text as="p" textStyle="textMd" textColor="gray-900" fontWeight="semiBold">
            Filter
          </Text>
        </Flex>
        {PostCount && <PostCount className="count-mobile" />}
      </Flex>
      <SidebarDemo />
      <Modal
        open={modalOpen}
        onClose={() => handleModalClose()}
        maxWidth="calc(100% - 40px)"
        width="100%"
        height="100%"
        marginX={20}
        containerStyles={`
          background: white;

          div[role="button"] {
            right: -3px;

            svg {
              color: black;
            }
          }

          .filter-fields {
            position: fixed;
            top: 80px;
            left: 20px;
            width: calc(100% - 20px);

            p {
              margin-bottom: 10px;
            }
          }

          .MuiFormControl-root {
            width: calc(100% - 20px);
            margin-bottom: 25px;
          }

          button {
            position: absolute;
            bottom: 30px;
            width: 100% !important;
            max-width: 100% !important;
          }

          [role=button] {
            right: 0;
          }
       `}
      >
        <Fields isModal />
        <ComponentButton
          label="Filter"
          buttonColor="Sunrise 500"
          isButton
          handleClick={() => handleModalButtonClick()}
        />
      </Modal>
    </Container>
  );
};

export default TemplateHubFilter;

const Container = styled.div`
  width: 100%;

  .sidebar-demo {
    display: none;
  }

  .filter-mobile {
    justify-content: space-between;

    p {
      font-size: 14px;
    }
  }

  .filter-mobile-btn {
    gap: 10px;

    &:hover {
      cursor: pointer;
    }
  }

  .filter-desktop {
    display: none;
    background: ${color.gray[200]};
    padding: 40px;
    box-shadow: ${shadow.xl};
    height: max-content;
    flex-direction: column;

    p {
      margin-bottom: 10px;
    }

    .MuiFormControl-root {
      width: 100%;
      margin-bottom: 15px;
    }

    .MuiFormControl-root ~ .MuiFormControl-root {
      margin-bottom: 0;
    }

    .MuiInputBase-root {
      background: white;
    }
  }

  ${media.lg} {
    .filter-desktop,
    .sidebar-demo {
      display: flex;
    }

    .filter-mobile {
      display: none;
    }
  }
`;
